import { useEffect, useRef, useState } from "react";
import api, { apiError } from "../../lib/api";
import { inputCls, labelCls, btnSecondary } from "./common";
import { MessageCircle, Loader2, Eye, Send, ChevronDown, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Per-page "WhatsApp Message After Payment" configuration (BLOCK 5-17, 36-40).
// Uses the existing WhatsApp app's APPROVED templates. Admin maps each template
// parameter to GDIOT PAY dynamic variables, previews, and can send a test.
export default function WhatsAppConfig({ value, onChange }) {
  const cfg = value || { enabled: false, template_name: "", language_code: "en", body_params: [], header_param: "", button_param: "" };
  const set = (patch) => onChange({ ...cfg, ...patch, updated_at: new Date().toISOString() });

  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState([]);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [testNo, setTestNo] = useState("");
  const [testing, setTesting] = useState(false);
  const [showTest, setShowTest] = useState(false);

  useEffect(() => {
    api.get("/whatsapp/templates")
      .then((r) => { setTemplates(r.data.templates || []); setVariables(r.data.variables || []); })
      .catch((e) => setError(apiError(e.response?.data?.detail) || "WhatsApp service unavailable"))
      .finally(() => setLoading(false));
  }, []);

  const selected = templates.find((t) => t.name === cfg.template_name);

  const pickTemplate = (name) => {
    const t = templates.find((x) => x.name === name);
    if (!t) { set({ template_name: "", body_params: [] }); return; }
    set({
      template_name: t.name, language_code: t.language || "en",
      body_params: Array.from({ length: t.body_param_count }, (_, i) => (cfg.body_params?.[i] ?? "")),
      header_param: t.header_has_var ? (cfg.header_param || "") : "",
      button_param: t.button_url_has_var ? (cfg.button_param || "") : "",
    });
    setPreview(null);
  };

  const setBodyParam = (i, val) => {
    const bp = [...(cfg.body_params || [])];
    bp[i] = val;
    set({ body_params: bp });
  };

  const doPreview = async () => {
    try {
      const { data } = await api.post("/whatsapp/preview", cfg);
      setPreview(data);
      if (!data.ok) toast.error("This message contains an unavailable field. Please update it before publishing.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const sendTest = async () => {
    if (!testNo.trim()) { toast.error("Enter a test WhatsApp number"); return; }
    setTesting(true);
    try {
      const { data } = await api.post("/whatsapp/test-send", {
        test_number: testNo, template_name: cfg.template_name, language_code: cfg.language_code,
        body_params: cfg.body_params, header_param: cfg.header_param, button_param: cfg.button_param,
      });
      toast.success(`Test message ${data.status || "sent"} to ${data.recipient}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setTesting(false);
  };

  return (
    <div className="space-y-4" data-testid="whatsapp-config">
      <div className="flex items-center justify-between">
        <h3 className="font-head font-semibold text-slate-900 flex items-center gap-2"><MessageCircle className="w-4 h-4 text-[#25d366]" /> WhatsApp Message After Payment</h3>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <span className="text-sm text-slate-600">{cfg.enabled ? "ON" : "OFF"}</span>
          <button type="button" data-testid="wa-enable-toggle" onClick={() => set({ enabled: !cfg.enabled })}
            className={`relative w-11 h-6 rounded-full transition-colors ${cfg.enabled ? "bg-[#25d366]" : "bg-slate-300"}`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${cfg.enabled ? "translate-x-5" : ""}`} />
          </button>
        </label>
      </div>
      <p className="text-xs text-slate-500">Sent automatically only after the payment is fully completed. The recipient is the registered mobile (WhatsApp) number.</p>

      {loading ? <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading WhatsApp templates…</div>
        : error ? <div className="text-sm text-rose-600 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {error}</div>
        : !cfg.enabled ? <div className="text-xs text-slate-400">Turn ON to configure the automatic message for this page.</div>
        : (
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Approved Template</label>
              <select className={inputCls} value={cfg.template_name || ""} onChange={(e) => pickTemplate(e.target.value)} data-testid="wa-template-select">
                <option value="">Select a template…</option>
                {templates.map((t) => <option key={t.name} value={t.name}>{t.name} ({t.body_param_count} field{t.body_param_count === 1 ? "" : "s"})</option>)}
              </select>
            </div>

            {selected && (
              <>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 whitespace-pre-wrap" data-testid="wa-template-body">{selected.body_text || "(no body text)"}</div>

                {selected.header_has_var && (
                  <ParamField label="Header value" value={cfg.header_param || ""} onChange={(v) => set({ header_param: v })} variables={variables} testid="wa-header-param" />
                )}
                {(cfg.body_params || []).map((bp, i) => (
                  <ParamField key={i} label={`Message field {{${i + 1}}}`} value={bp} onChange={(v) => setBodyParam(i, v)} variables={variables} testid={`wa-body-param-${i}`} />
                ))}
                {selected.button_url_has_var && (
                  <ParamField label="Button link value" value={cfg.button_param || ""} onChange={(v) => set({ button_param: v })} variables={variables} testid="wa-button-param" />
                )}

                <div className="flex flex-wrap gap-2">
                  <button type="button" className={btnSecondary} onClick={doPreview} data-testid="wa-preview-btn"><Eye className="w-4 h-4" /> Preview Message</button>
                  <button type="button" className={btnSecondary} onClick={() => setShowTest((s) => !s)} data-testid="wa-test-toggle"><Send className="w-4 h-4" /> Send Test Message</button>
                </div>

                {preview && (
                  <div className={`rounded-lg p-3 text-sm border ${preview.ok ? "bg-[#e7ffdb] border-[#25d366]/40" : "bg-rose-50 border-rose-200"}`} data-testid="wa-preview-box">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Preview (sample values)</div>
                    {!preview.ok && <div className="text-rose-600 text-xs mb-1">Unavailable fields: {preview.unresolved.map((u) => `{{${u}}}`).join(", ")}</div>}
                    {preview.header && <div className="font-semibold">{preview.header}</div>}
                    <div className="whitespace-pre-wrap text-slate-700">{(selected.body_text || "").replace(/\{\{(\d+)\}\}/g, (_, n) => preview.body_params[Number(n) - 1] ?? "")}</div>
                    {preview.button && <div className="mt-1 text-blue-600 underline break-all">{preview.button}</div>}
                  </div>
                )}

                {showTest && (
                  <div className="flex flex-wrap items-end gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="wa-test-box">
                    <div className="flex-1 min-w-[180px]">
                      <label className={labelCls}>Test WhatsApp Number</label>
                      <input className={inputCls} value={testNo} onChange={(e) => setTestNo(e.target.value)} placeholder="+91XXXXXXXXXX" data-testid="wa-test-number" />
                    </div>
                    <button type="button" onClick={sendTest} disabled={testing} className="px-4 py-2.5 rounded-lg bg-[#25d366] text-white font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="wa-send-test-btn">{testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Test</button>
                    <p className="text-[11px] text-slate-400 w-full">Uses sample values. No registration is created and no payment is marked completed.</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
    </div>
  );
}

function ParamField({ label, value, onChange, variables, testid }) {
  const ref = useRef();
  const [open, setOpen] = useState(false);
  const insert = (key) => {
    const el = ref.current;
    const token = `{{${key}}}`;
    if (el && typeof el.selectionStart === "number") {
      const s = el.selectionStart, e = el.selectionEnd;
      onChange((value || "").slice(0, s) + token + (value || "").slice(e));
    } else {
      onChange((value || "") + token);
    }
    setOpen(false);
  };
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="flex gap-2">
        <input ref={ref} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Type text or insert a variable…" data-testid={testid} />
        <div className="relative">
          <button type="button" className={btnSecondary} onClick={() => setOpen((o) => !o)} data-testid={`${testid}-insert`}>Insert Variable <ChevronDown className="w-3 h-3" /></button>
          {open && (
            <div className="absolute right-0 z-20 mt-1 w-56 max-h-64 overflow-auto bg-white border border-slate-200 rounded-lg shadow-lg">
              {variables.map((v) => (
                <button type="button" key={v.key} onClick={() => insert(v.key)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex justify-between">
                  <span>{v.label}</span><span className="text-slate-300 font-mono text-xs">{`{{${v.key}}}`}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
